import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { BulkDialog, DeliverablesPhasesList } from "..";
import {
	CategoryService,
	DeliverablesService,
	DocumentOutService,
	CoveragePhaseService,
	ProjectTeamService,
	ApiService,
} from "../../../../../../../../../api";
import {
	CircularLoader,
	CommonFilters,
	CustomButton,
	DebounceSearchTextField,
	FiltersWrapper,
	generateFilters,
	I18nTranslate,
	icon,
	IconComponent,
	ProjectCategoriesFilter,
	ViewTabs,
} from "../../../../../../../../../common/components";
import {
	Permissions,
	SegFlags,
	hasCompanyPermission,
	hasPermission,
	isSegFeatureEnabled,
	translate,
} from "../../../../../../../../../common/providers";
import { isNonEmptyArray, isNonEmptyObject, updateTreeBranch } from "../../../../../../../../../common/utils";
import styles from "./DeliverablesTabLayout.module.css";
import SmartReviewContext from "../../../../../../../context/smart-review-context";
import { useApi } from "../../../../../../../../../common/hooks";
import { setDisableKeyEvents } from "../../../../../../../../../common/slice";
import DocumentDialog from "./document-dialog/DocumentDialog";
import { DOCUMENT_STATUSES } from "../../../../../../../constants/constants";

export default function DeliverablesTabLayout({ onSetSelectedTab, reqId, selectedTab }) {
	const { call: getTypes } = useApi(DocumentOutService.getTypes);
	const { call: getFormats } = useApi(DocumentOutService.getFormats);
	const { call: getSelectedCount } = useApi(DeliverablesService.getSelectedCount);
	const { call: getDeliverableCount } = useApi(DocumentOutService.getDeliverableCount);
	const { call: getPhasesTree } = useApi(DeliverablesService.getPhasesTree);
	const { call: getAllDeliverablesByProjectAndPhase } = useApi(
		DeliverablesService.getAllDeliverablesByProjectAndPhase
	);
	const { call: getDeliverablesCount } = useApi(DeliverablesService.getDeliverablesCount);
	const { call: getAllPhasesByProject } = useApi(DeliverablesService.getAllPhasesByProject);
	const { call: getAllByProject } = useApi(CoveragePhaseService.getAllByProject);
	const { call: getTreeChildrenForProject } = useApi(CategoryService.getTreeChildrenForProject);
	const { call: getTreeParentsForProject } = useApi(CategoryService.getTreeParentsForProject);
	const { call: searchUsersInProjectAndCompany } = useApi(ProjectTeamService.searchUsersInProjectAndCompany);
	const isArchived = useSelector(({ srDocument }) => srDocument.status === DOCUMENT_STATUSES.ARCHIVED);
	const [isLoadingDeliverables, setIsLoadingDeliverables] = useState(false);
	const [phasesTree, setPhasesTree] = useState([]);
	const [selectedCount, setSelectedCount] = useState(0);
	const [filters, setFilters] = useState({ name: "", categories: {} });
	const [openDeliverablesDialog, setOpenDeliverablesDialog] = useState(false);
	const [phasesForCreation, setPhasesForCreation] = useState([]);
	const [fbs, setFbs] = useState([]);
	const [owners, setOwners] = useState([]);
	const [documentFormats, setDocumentFormats] = useState([]);
	const [documentTypes, setDocumentTypes] = useState([]);
	const [filtersPreviewRequest, setFiltersPreviewRequest] = useState(null);
	const [bulkAllocateOpen, setBulkAllocateOpen] = useState(false);
	const [totalDelivCount, setTotalDelivCount] = useState(0);
	const hasFilters = useMemo(() => filters.name.length > 0 || filters.categories?.CATEGORY?.length > 0, [filters]);
	const dispatch = useDispatch();
	const deliverablesCount = useMemo(() => {
		if (hasFilters) {
			let count = 0;
			phasesTree.forEach((phase) => (count += phase.deliverables?.length || 0));
			return count;
		}
		return 0;
	}, [hasFilters, phasesTree]);
	const hasCreationPermission = useMemo(
		() =>
			hasCompanyPermission([Permissions.PROJECT_LEADER]) &&
			hasPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]),
		[]
	);
	const tabs = useMemo(
		() => [
			{
				label: translate("smart-review.cmp-details.option-panel.deliverables-tab.edit.deliverables-list.all"),
				id: "ALL",
			},
			{
				label: translate(
					"smart-review.cmp-details.option-panel.deliverables-tab.edit.deliverables-list.selected",
					{
						checkedCount: selectedCount,
					}
				),
				id: "SELECTED",
				disabled: selectedCount === 0,
			},
		],
		[selectedCount]
	);
	const projectId = useSelector(({ context }) => context.project.id);
	const fetchDocumentTypes = useCallback(() => {
		getTypes()
			.then((data) => {
				if (Array.isArray(data)) {
					setDocumentTypes(data);
				}
			})
			.catch((err) => {
				console.error(err);
			});
	}, [getTypes]);
	const fetchDocumentFormats = useCallback(() => {
		getFormats()
			.then((data) => {
				if (Array.isArray(data)) {
					setDocumentFormats(data);
				}
			})
			.catch((err) => {
				console.error(err);
			});
	}, [getFormats]);
	const fetchPhases = useCallback(() => {
		setIsLoadingDeliverables(true);
		getAllPhasesByProject({ projectId })
			.then((data) => setPhasesTree(data))
			.catch((err) => {
				console.error(err);
			})
			.finally(() => setIsLoadingDeliverables(false));
	}, [getAllPhasesByProject, projectId]);
	const fetchFBS = useCallback(() => {
		getTreeParentsForProject({ projectId, details: false })
			.then((data) => {
				setFbs(data);
			})
			.catch((err) => {
				console.error(err);
			});
	}, [getTreeParentsForProject, projectId]);
	const fetchProjectUsers = useCallback(() => {
		searchUsersInProjectAndCompany({ projectId }, "")
			.then((data) => {
				if (Array.isArray(data)) {
					setOwners(data);
				}
			})
			.catch((err) => {
				console.error(err);
			});
	}, [searchUsersInProjectAndCompany, projectId]);
	const fetchPhasesForCreation = useCallback(() => {
		getAllByProject({ projectId })
			.then((data) => {
				setPhasesForCreation(data);
			})
			.catch((err) => {
				console.error(err);
			});
	}, [getAllByProject, projectId]);
	const fetchPhasesTreeWithFilters = useCallback(() => {
		if (hasFilters || selectedTab === "SELECTED") {
			setIsLoadingDeliverables(true);
			Promise.all([
				getPhasesTree(
					{ projectId },
					{ filters: { ...generateFilters(filters), isSelected: selectedTab === "SELECTED" }, reqId }
				),
				getAllPhasesByProject({ projectId }),
			])
				.then(([phasesTreeData, phasesData]) => {
					const phasesMap = new Map();
					if (Array.isArray(phasesData)) {
						phasesData.forEach((phase) => {
							phasesMap.set(phase.phaseId, {
								lockedOrArchived: phase.lockedOrArchived || phase.phaseLockedOrArchived || phase.locked,
							});
						});
					}
					// Merge locked status into phasesTreeData
					const enrichedPhases = Array.isArray(phasesTreeData)
						? phasesTreeData.map((phase) => {
								const phaseInfo = phasesMap.get(phase.phaseId);
								if (phaseInfo) {
									return {
										...phase,
										lockedOrArchived: phaseInfo.lockedOrArchived,
									};
								}
								return phase;
						  })
						: phasesTreeData;
					setPhasesTree(enrichedPhases);
				})
				.catch((err) => {
					console.error(err);
				})
				.finally(() => {
					setIsLoadingDeliverables(false);
				});
		}
	}, [filters, getPhasesTree, getAllPhasesByProject, hasFilters, projectId, reqId, selectedTab]);
	useEffect(() => {
		if (hasCreationPermission) {
			fetchDocumentTypes();
			fetchDocumentFormats();
			fetchFBS();
			fetchProjectUsers();
			fetchPhasesForCreation();
		}
	}, [
		hasCreationPermission,
		fetchDocumentTypes,
		fetchDocumentFormats,
		fetchFBS,
		fetchProjectUsers,
		fetchPhasesForCreation,
	]);
	useEffect(() => {
		if (!hasFilters && selectedTab === "ALL") {
			fetchPhases();
		}
	}, [hasFilters, fetchPhases, selectedTab, reqId]);
	useEffect(() => {
		getSelectedCount({ reqId })
			.then((data) => {
				setSelectedCount(data.value);
			})
			.catch((err) => {
				console.error(err);
			});
	}, [reqId, getSelectedCount]);
	useEffect(() => {
		setFiltersPreviewRequest(
			() => (previewFilters) =>
				getDeliverablesCount(
					{ projectId },
					{
						filters: { ...generateFilters(previewFilters), isSelected: selectedTab === "SELECTED" },
						reqId,
					}
				).then((data) => data.value)
		);
	}, [projectId, getDeliverablesCount, reqId, selectedTab]);
	useEffect(() => {
		fetchPhasesTreeWithFilters();
	}, [fetchPhasesTreeWithFilters]);
	useEffect(() => {
		if (projectId) {
			getDeliverableCount({ projectId })
				.then((data) => setTotalDelivCount(data.value))
				.catch(console.error);
		}
	}, [getDeliverableCount, projectId]);
	const handleExpandPhase = useCallback(
		({ expandablePhase, expandablePhaseId }) => {
			if (!expandablePhaseId && expandablePhase.deliverablesCount === expandablePhase.deliverables?.length) {
				return;
			}
			const phaseId = expandablePhaseId || expandablePhase.phaseId;
			getAllDeliverablesByProjectAndPhase({ projectId, phaseId, reqId })
				.then((data) => {
					const newPhases = phasesTree.map((phase) => {
						if (phase.phaseId === phaseId) {
							return { ...phase, defaultExpanded: true, deliverables: data };
						}
						return phase;
					});
					setPhasesTree(newPhases);
				})
				.catch(console.error);
		},
		[phasesTree, getAllDeliverablesByProjectAndPhase, projectId, reqId]
	);
	const createDocument = useCallback(
		async (payload) => {
			const cancelTokenSource = ApiService.getCancelTokenSource();
			try {
				await DocumentOutService.create({ projectId }, payload, cancelTokenSource.token);
				if (payload?.phaseId) {
					const newPhases = phasesTree.map((phase) => {
						if (phase.phaseId === payload.phaseId) {
							return {
								...phase,
								deliverablesCount: phase.deliverablesCount + 1,
							};
						}
						return phase;
					});
					setPhasesTree(newPhases);
					handleExpandPhase({ expandablePhaseId: payload.phaseId });
				}
			} catch (error) {
				console.error("Error creating deliverable:", error);
			}
		},
		[projectId, phasesTree, handleExpandPhase]
	);

	const handleOpenAddDeliverablesDialog = () => {
		setOpenDeliverablesDialog(true);
	};
	const handleCloseAddDeliverablesDialog = () => {
		setOpenDeliverablesDialog(false);
	};
	const handleExpandCategoryRow = (category) => {
		if (!isNonEmptyArray(category.subThematic)) {
			getTreeChildrenForProject({ parentId: category.id, details: false })
				.then((data) => {
					setFbs((prev) =>
						updateTreeBranch({
							tree: prev,
							idToReplace: category.id,
							newBranch: {
								...category,
								subThematic: data,
							},
							iterativeBranchName: "subThematic",
						})
					);
				})
				.catch((err) => {
					console.error(err);
				});
		}
	};
	const handleChangeSearchText = (value) => {
		setFilters((prev) => ({ ...prev, name: value }));
	};
	const handleChangeShowSelectedTab = (_, tab) => {
		onSetSelectedTab(tab);
	};
	const handleApply = (newFilters) => {
		setFilters(newFilters);
	};
	const handleRemove = (newFilters) => {
		SmartReviewContext.updateFilterInStorage(newFilters);
		const { separator, Type, ...rest } = newFilters;
		if (!isNonEmptyObject(rest)) {
			SmartReviewContext.setContentSelection(null);
		}
	};
	const customLabelApply = (results) => {
		if (results <= 1 && results !== null) {
			return translate("common:component.filter.apply", { results });
		}
		if (results > 1) {
			return translate("common:component.filters.apply", { results });
		}
		return translate("common:btn.apply");
	};
	const handleAllocate = (delivId, added) => {
		phasesTree.forEach((phase) => {
			if (isNonEmptyArray(phase.deliverables)) {
				phase.deliverables.forEach((deliverable) => {
					if (deliverable.id === delivId) {
						if (added) {
							deliverable.isCovered = false;
						} else {
							deliverable.isCovered = undefined;
						}
					}
				});
			}
		});
	};
	const handleOpenBulkAllocate = () => {
		setBulkAllocateOpen(true);
	};
	const handleCloseBulkAllocate = () => {
		setBulkAllocateOpen(false);
	};
	const handleUpdateBulk = ({ addedCount, removedCount }) => {
		setSelectedCount((prev) => prev + addedCount - removedCount);
		fetchPhasesTreeWithFilters();
	};
	return (
		<>
			<div className={styles.container_filter}>
				<div>
					<ViewTabs
						className={styles.tabs}
						selectedTab={selectedTab}
						tabIdentifier="id"
						tabs={tabs}
						onChange={handleChangeShowSelectedTab}
					/>
				</div>
			</div>
			<div className={styles.container_search}>
				<DebounceSearchTextField
					initValue={filters.name}
					isLoading={isLoadingDeliverables}
					placeholder={translate("common:deliverables.search-textfield.placeholder")}
					onBlur={() => dispatch(setDisableKeyEvents(false))}
					onChange={handleChangeSearchText}
					onFocus={() => dispatch(setDisableKeyEvents(true))}
				/>
				{hasCreationPermission && (
					<CustomButton
						color="secondary"
						disabled={isArchived}
						startIcon={icon.faPlus}
						variant="outlined"
						onClick={handleOpenAddDeliverablesDialog}
					>
						{translate(
							"smart-review.cmp-details.option-panel.deliverables-tab.edit.deliverables-list.button.new"
						)}
					</CustomButton>
				)}
			</div>
			<div className={styles.filterBulkContainer}>
				<div className={styles.filterContainer}>
					<FiltersWrapper
						multiline
						className={styles.filters}
						components={[
							{
								default: true,
								enabled: isSegFeatureEnabled(SegFlags.CATEGORY),
								component: CommonFilters.CATEGORIES,
								renderer: <ProjectCategoriesFilter disableFavoriteCategories />,
								hasFavorites: true,
							},
						]}
						customLabelApply={customLabelApply}
						defaultFilters={filters}
						disablePortal={false}
						filterPopUpPlacement="auto"
						previewRequest={filtersPreviewRequest}
						onApply={handleApply}
						onRemove={handleRemove}
					/>
				</div>
				{hasFilters && isNonEmptyArray(phasesTree) && (
					<CustomButton className={styles.bulkButton} variant="outlined" onClick={handleOpenBulkAllocate}>
						{translate("smart-review.cmp-details.option-panel.deliverables-tab.bulk-allocate")}
					</CustomButton>
				)}
			</div>
			{hasFilters && (
				<div className={styles.counter}>
					<I18nTranslate
						param={{ deliverablesCount, totalCount: totalDelivCount }}
						translationKey={
							deliverablesCount === 1
								? "common:deliverables-count.deliverable-on"
								: "common:deliverables-count.deliverables-on"
						}
					/>
				</div>
			)}
			{isNonEmptyArray(phasesTree) && !isLoadingDeliverables ? (
				<div className={styles.listContainer}>
					<DeliverablesPhasesList
						hasFilters={hasFilters}
						phases={phasesTree}
						reqId={reqId}
						setSelectedCount={setSelectedCount}
						showSelected={selectedTab === "SELECTED"}
						onAllocate={handleAllocate}
						onExpandPhase={handleExpandPhase}
					/>
				</div>
			) : (
				(isLoadingDeliverables && (
					<div className={styles.loaderContainer}>
						<CircularLoader />
					</div>
				)) || (
					<div className={styles.noContent}>
						<IconComponent
							className={styles.noContent__infoIcon}
							color="var(--blue-500)"
							icon={icon.faInfoCircle}
						/>
						{translate("common:component.filters.deliverables.empty-state")}
					</div>
				)
			)}
			<DocumentDialog
				action={createDocument}
				fbs={fbs}
				formats={documentFormats}
				open={openDeliverablesDialog}
				owners={owners}
				phases={phasesForCreation}
				types={documentTypes}
				onClose={handleCloseAddDeliverablesDialog}
				onExpand={handleExpandCategoryRow}
			/>
			<BulkDialog
				open={bulkAllocateOpen}
				phasesTree={phasesTree}
				reqId={reqId}
				onClose={handleCloseBulkAllocate}
				onUpdateBulk={handleUpdateBulk}
			/>
		</>
	);
}
